import { Toaster } from "sonner";
import { useThemeStore } from "../../stores/theme-store";

/**
 * The app's single toast surface.
 *
 * Extends the existing sonner setup rather than replacing it — there are already ~60 `toast.*`
 * call sites across nine files, and a second notification system would mean two stacking
 * contexts fighting over the same corner of the screen.
 *
 * What this adds over the previous inline `<Toaster theme="dark" ... />`:
 *
 *  - Theme follows the app. It was hardcoded to "dark", so every toast stayed dark after Light
 *    mode shipped — dark-on-light panels in the corner of an otherwise light page.
 *  - Errors last longer than successes. "Contact deleted" needs a glance; "Failed to create API
 *    key — name already in use" needs reading, and 4s is not enough to read and act on it.
 *  - A close button, so a stack of errors can be cleared rather than waited out.
 *
 * Position is bottom-centre. It carries a bottom offset rather than sitting flush: every table
 * page pins a pagination bar to bottom-0, and a flush toast would land directly on top of the
 * page controls — covering the very buttons a user reaches for after an action completes. The
 * offset clears that bar so the toast sits above it instead of over it.
 *
 * richColors and the stacking behaviour are left exactly as they were: sonner's queueing already
 * handles rapid-fire toasts without overlap.
 */
export function AppToaster() {
  const resolved = useThemeStore((s) => s.resolved);

  return (
    <Toaster
      theme={resolved}
      position="bottom-center"
      // Sits low, close to the bottom edge. It does overlap the sticky pagination bar on table
      // pages — but only across its middle, which is empty: that bar puts its range text hard
      // left and its page controls hard right, so a centred toast lands in the gap between them
      // and covers no control. Toasts are transient, the bar is not.
      offset="1.5rem"
      richColors
      closeButton
      // Sonner's default slide comes from the edge it is docked to; from bottom-centre that is a
      // rise. Reduced motion is handled by the global guard in globals.css, which collapses the
      // animation to a fade.
      visibleToasts={4}
      // Success is an acknowledgement and can go quickly; an error is information the user has to
      // read, and often act on, so it gets meaningfully longer.
      // Per-type durations are set in lib/toast.ts, because sonner's duration prop is a single
      // global value and cannot differ by type on its own.
    />
  );
}
